import classNames from 'clsx';
import type React from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { InfoIcon } from 'ui-components/components/info-icon';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { OriginCountryDropdown } from './origin-country-dropdown';
import Global from '../../../../../store/global-selectors';
import { HarmonizationSuggestion } from '../../../../harmonization/harmonization-suggestion';
import { useIsHarmonizationSuggestionEnabled } from '../../../../harmonization/use-is-harmonization-suggestion-enabled';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { useEnableCpscCertificateFields } from '../../../../../feature-flags/hooks/use-enable-cpsc-certificate-fields';
import { messages } from '../../../messages';
import style from './customs-declarations.module.css';
import utilityStyle from '../../../../../css-variables/utilities.module.css';
import { MID_CODE_MAX_LENGTH, CPSC_CERTIFICATE_FIELD_MAX_LENGTH } from '../../../validation/schema';

export const ProductDetailsCustomsDeclarationsV3 = () => {
  const {
    productId,
    userHasPermission,
    noAutoCustomsDeclaration,
    setNoAutoCustoms,
    customsDescription,
    setCustomsDescription,
    customsDeclaredValue,
    setCustomsDeclaredValue,
    customsHarmonizationCode,
    setCustomsHarmonizationCode,
    customsMIDCode,
    setCustomsMIDCode,
    nsManufacturerProductId,
    setNsManufacturerProductId,
    cpscCertificateVersionId,
    setCpscCertificateVersionId,
    cpscCertifierId,
    setCpscCertifierId,
    validationErrors,
  } = useProductDetailsContext();
  const intl = useIntl();
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const isHarmonizationSuggestionEnabled = useIsHarmonizationSuggestionEnabled();
  const isCpscEnabled = useEnableCpscCertificateFields();

  const isCustomsDeclarationEnabled = !noAutoCustomsDeclaration;

  const onCustomsDeclarationToggle = useCallback(
    (val: boolean) => {
      setNoAutoCustoms(!val);
      // Clear only invalid fields when disabling customs — valid data is preserved
      // so toggling off and back on doesn't lose it.
      if (!val) {
        if (validationErrors?.customsDeclaredValue) {
          setCustomsDeclaredValue(null);
        }
        if (validationErrors?.customsMIDCode) {
          setCustomsMIDCode('');
        }
      }
    },
    [setNoAutoCustoms, setCustomsDeclaredValue, setCustomsMIDCode, validationErrors],
  );

  const onCustomsValueChange = useCallback(
    (value: unknown) => {
      setCustomsDeclaredValue(value);
    },
    [setCustomsDeclaredValue],
  );

  const onMIDCodeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustomsMIDCode(event.target.value);
    },
    [setCustomsMIDCode],
  );

  const onNsManufacturerProductIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNsManufacturerProductId(event.target.value);
    },
    [setNsManufacturerProductId],
  );

  const onCpscCertificateVersionIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCpscCertificateVersionId(event.target.value);
    },
    [setCpscCertificateVersionId],
  );

  const onCpscCertifierIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCpscCertifierId(event.target.value);
    },
    [setCpscCertifierId],
  );

  return (
    <div className={style['customs-tab']}>
      <div className={style['content-card']}>
        {/* Toggle: Enable a customs declaration for this product */}
        <div className={style['toggle-section']}>
          <CheckboxSlider
            checkboxId={`${productId}-customs-declaration`}
            checked={isCustomsDeclarationEnabled}
            disabled={!userHasPermission}
            labelText={intl.formatMessage(messages.customsEnableDeclarationLabel)}
            onChange={onCustomsDeclarationToggle}
          />
        </div>

        {/* Card header + fields */}
        {isCustomsDeclarationEnabled ? (
          <div className={style.section}>
            <Text element='div' textType={TextType.H4} value={messages.customsDeclarationLabel} />
            <Text className={style.subtitle} element='div' textType={TextType.Caption} value={messages.customsDeclarationSubtitle} />

            {/* Top 2-column grid: Description | HS Code */}
            <div className={style['two-col-grid']}>
              {/* Description column */}
              <div>
                <Text className={style.label} element='div' textType={TextType.Label} value={messages.customsDescriptionHelper} />
                <div className={style['description-block']}>
                  <FloatTextArea
                    className={style['description-textarea']}
                    data-testid='customs-description'
                    disabled={!userHasPermission}
                    onChange={setCustomsDescription}
                    placeholder={intl.formatMessage(messages.customsDescriptionExample)}
                    value={customsDescription ?? ''}
                  />
                  {isHarmonizationSuggestionEnabled ? (
                    <div className={style['suggest-row']}>
                      <HarmonizationSuggestion onCodeSelected={setCustomsHarmonizationCode} />
                    </div>
                  ) : null}
                </div>
                <Text
                  className={style['description-footer']}
                  element='div'
                  textType={TextType.Caption}
                  value={messages.customsDescriptionFooter}
                />
              </div>

              {/* HS Code column */}
              <div>
                <FlexRow className={style.label} gap={Gap.Space4}>
                  <Text element='div' textType={TextType.Label} value={messages.customsHarmonization} />
                  <InfoIcon
                    color={THEME_CONSTANTS.unstyled}
                    tooltipText={<Text textType={TextType.Caption} value={messages.customsHarmonizationInfo} />}
                  />
                </FlexRow>
                <Input
                  className={classNames(style['input-wrapper'], utilityStyle['input-border'])}
                  data-testid='customs-harmonization'
                  disabled={!userHasPermission}
                  onChange={setCustomsHarmonizationCode}
                  value={customsHarmonizationCode}
                />
              </div>
            </div>

            {/* Declared Value — full row */}
            <div className={style['field-group']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.declaredValue} />
              <div className={style['declared-value-input']}>
                <CurrencyInput
                  className={classNames(style['currency-input'], utilityStyle['input-border'])}
                  currencyCode={currencyCode}
                  data-testid='customs-declared-value'
                  disabled={!userHasPermission}
                  onChange={onCustomsValueChange}
                  value={customsDeclaredValue ?? ''}
                />
                {validationErrors?.customsDeclaredValue?.message ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>{validationErrors.customsDeclaredValue.message}</InlineMessage>
                ) : null}
              </div>
            </div>

            {/* Bottom 2-column grid: MID Code | Country of Origin */}
            <div className={style['two-col-grid']}>
              {/* MID Code column */}
              <div>
                <FlexRow className={style.label} gap={Gap.Space4}>
                  <Text element='div' textType={TextType.Label} value={messages.customsMIDCode} />
                  <InfoIcon
                    color={THEME_CONSTANTS.unstyled}
                    tooltipText={<Text textType={TextType.Caption} value={messages.customsMIDCodeInfo} />}
                  />
                </FlexRow>
                <Input
                  className={style['input-wrapper']}
                  data-testid='customs-mid-code'
                  disabled={!userHasPermission}
                  inlineMessage={validationErrors?.customsMIDCode?.message}
                  inlineMessageType={validationErrors?.customsMIDCode?.message ? INLINE_MESSAGE_TYPES.error : undefined}
                  maxLength={MID_CODE_MAX_LENGTH}
                  onChange={onMIDCodeChange}
                  value={customsMIDCode ?? ''}
                  version={version2}
                />
              </div>

              {/* Country of Origin column */}
              <div>
                <Text className={style.label} element='div' textType={TextType.Label} value={messages.customsOriginCountry} />
                <OriginCountryDropdown />
              </div>
            </div>

            {/* CPSC certificate fields (Certificate Version ID | Certifier ID) */}
            {isCpscEnabled ? (
              <div className={style['two-col-grid']}>
                <div>
                  <Text className={style.label} element='div' textType={TextType.Label} value={messages.cpscCertificateVersionId} />
                  <Input
                    className={style['input-wrapper']}
                    data-testid='cpsc-certificate-version-id'
                    disabled={!userHasPermission}
                    maxLength={CPSC_CERTIFICATE_FIELD_MAX_LENGTH}
                    onChange={onCpscCertificateVersionIdChange}
                    value={cpscCertificateVersionId ?? ''}
                    version={version2}
                  />
                </div>

                <div>
                  <Text className={style.label} element='div' textType={TextType.Label} value={messages.cpscCertifierId} />
                  <Input
                    className={style['input-wrapper']}
                    data-testid='cpsc-certifier-id'
                    disabled={!userHasPermission}
                    maxLength={CPSC_CERTIFICATE_FIELD_MAX_LENGTH}
                    onChange={onCpscCertifierIdChange}
                    value={cpscCertifierId ?? ''}
                    version={version2}
                  />
                </div>
              </div>
            ) : null}

            {/* NS-PID (Non-Standardized Manufacturer Product ID) — distinct from the MID Code above (SPD-28496) */}
            <div className={style['two-col-grid']}>
              <div>
                <FlexRow className={style.label} gap={Gap.Space4}>
                  <Text element='div' textType={TextType.Label} value={messages.nsManufacturerProductId} />
                  <InfoIcon
                    color={THEME_CONSTANTS.unstyled}
                    tooltipText={<Text textType={TextType.Caption} value={messages.nsManufacturerProductIdInfo} />}
                  />
                </FlexRow>
                <Input
                  className={style['input-wrapper']}
                  data-testid='ns-manufacturer-product-id'
                  disabled={!userHasPermission}
                  onChange={onNsManufacturerProductIdChange}
                  value={nsManufacturerProductId ?? ''}
                  version={version2}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
